/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Modal } from 'antd';

import { useMessage } from '@/hooks/message';
import { useDeleteAlertConfigMutation } from '@/hooks/mutation/useDeleteAlertConfigMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { tokens } from '@/theme';

type DeleteAlertConfigModalProps = {
  alertId: string | undefined;
  alertName: string | undefined;
  onCancelDeletion: () => void;
  onDeleteSuccess?: () => void;
};

export function DeleteAlertConfigModal({
  alertId,
  alertName,
  onCancelDeletion,
  onDeleteSuccess,
}: DeleteAlertConfigModalProps) {
  const { t } = useMimicTranslation('notificationManagement');

  const [message] = useMessage();

  const { mutate: deleteAlert, isPending } = useDeleteAlertConfigMutation({
    onError: () => {
      message.error(t('details.deleteError'));
    },
    onSuccess: () => {
      message.success(t('details.deleteSuccess'));
      closeDeleteDialog();
      onDeleteSuccess?.();
    },
  });

  const onAlertDelete = () => {
    if (alertId) {
      deleteAlert(alertId);
    }
  };

  const closeDeleteDialog = () => {
    onCancelDeletion();
  };

  return (
    <Modal
      open={Boolean(alertId)}
      onCancel={onCancelDeletion}
      onOk={onAlertDelete}
      cancelText={t('confirmDelete.cancel')}
      cancelButtonProps={{ disabled: isPending }}
      okText={t('confirmDelete.ok')}
      okButtonProps={{
        loading: isPending,
        style: { backgroundColor: tokens.color.red.red07, borderColor: tokens.color.red.red07 },
      }}
    >
      <Container padding="sm">
        <Title level={4}>{t('confirmDelete.title', { name: alertName })}</Title>
        <Text>{t('confirmDelete.body1')}</Text>
        <Text>{t('confirmDelete.body2')}</Text>
      </Container>
    </Modal>
  );
}
